/**
 * Storage API functions for OBS operations.
 */

import apiClient, { getAccessToken } from './client';
import type { SignedUrlResponse, MultipartInitResponse } from '@/types';
import { uploadWarn, uploadError } from '@/utils/uploadLogger';

/**
 * Get signed URL for file upload.
 */
export const getUploadUrl = async (
  fileName: string,
  contentType: string
): Promise<SignedUrlResponse> => {
  const response = await apiClient.post<SignedUrlResponse>(
    '/storage/upload-url/',
    {
      file_name: fileName,
      content_type: contentType,
    },
    {
      // Usually fast, but give extra time in case backend/OBS is slow
      timeout: 180_000,  // 3 minutes
    }
  );
  return response.data;
};

/**
 * Proxy download a file via backend streaming (for external users who cannot access OBS directly).
 * Uses browser-native download via <a> tag with JWT token in query param to avoid
 * buffering the entire file in JS heap memory (which silently fails for large files).
 */
export const buildProxyDownloadUrl = (fileUrl: string, fileName: string): string => {
  const token = getAccessToken();
  const params = new URLSearchParams({ file_url: fileUrl, file_name: fileName });
  if (token) params.set('token', token);
  return `${window.location.origin}/api/v1/storage/proxy-download/?${params}`;
};

export const proxyDownloadFile = (fileUrl: string, fileName: string): void => {
  const link = document.createElement('a');
  link.href = buildProxyDownloadUrl(fileUrl, fileName);
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Get signed URL for file download.
 */
export const getDownloadUrl = async (fileUrl: string): Promise<SignedUrlResponse> => {
  const response = await apiClient.post<SignedUrlResponse>(
    '/storage/download-url/',
    {
      file_url: fileUrl,
    },
    {
      // Usually fast, but give extra time in case backend/OBS is slow
      timeout: 180_000,  // 3 minutes
    }
  );
  return response.data;
};

/**
 * Initialize multipart upload for large files.
 */
export const initMultipartUpload = async (
  fileName: string,
  contentType: string,
  fileSize: number,
  chunkSize?: number  // Optional: dynamic chunk size (10MB or 100MB)
): Promise<MultipartInitResponse> => {
  const requestData: {
    file_name: string;
    content_type: string;
    file_size: number;
    chunk_size?: number;
  } = {
    file_name: fileName,
    content_type: contentType,
    file_size: fileSize,
  };

  // Include chunk_size only if provided
  if (chunkSize !== undefined) {
    requestData.chunk_size = chunkSize;
  }

  const response = await apiClient.post<MultipartInitResponse>(
    '/storage/multipart/init/',
    requestData,
    {
      // Large files need time to generate many signed URLs (e.g., 10GB file = 100 parts)
      // Backend calls OBS API for each part, which can take 1-2s per URL when OBS is slow
      // Timeout: 5 minutes to handle large file initialization
      timeout: 300_000,  // 5 minutes
    }
  );
  return response.data;
};

/**
 * Refresh signed URLs for an existing multipart upload.
 * Does NOT create a new upload session.
 */
export const refreshMultipartUrls = async (
  uploadId: string,
  objectKey: string,
  fileSize: number,
  chunkSize?: number  // Optional: dynamic chunk size (10MB or 100MB)
): Promise<{ part_urls: Array<{ part_number: number; url: string }> }> => {
  const requestData: {
    upload_id: string;
    object_key: string;
    file_size: number;
    chunk_size?: number;
  } = {
    upload_id: uploadId,
    object_key: objectKey,
    file_size: fileSize,
  };

  // Include chunk_size only if provided
  if (chunkSize !== undefined) {
    requestData.chunk_size = chunkSize;
  }

  const response = await apiClient.post<{ part_urls: Array<{ part_number: number; url: string }> }>(
    '/storage/multipart/refresh-urls/',
    requestData,
    {
      // Resume upload needs to refresh signed URLs for remaining parts
      // Same logic as initMultipartUpload - backend generates URLs which can be slow
      // Timeout: 5 minutes to handle large file URL refresh
      timeout: 300_000,  // 5 minutes
    }
  );
  return response.data;
};

/**
 * Complete multipart upload.
 */
export const completeMultipartUpload = async (
  uploadId: string,
  objectKey: string,
  parts: Array<{ part_number: number; etag: string }>,
  metadata?: {
    file_name?: string;
    file_size?: number;
    start_time?: string;
    mode?: string;
    chunk_size?: string;
  }
): Promise<{ file_url: string }> => {
  const requestData: {
    upload_id: string;
    object_key: string;
    parts: Array<{ part_number: number; etag: string }>;
    file_name?: string;
    file_size?: number;
    start_time?: string;
    mode?: string;
    chunk_size?: string;
  } = {
    upload_id: uploadId,
    object_key: objectKey,
    parts,
  };

  // Include metadata if provided
  if (metadata) {
    if (metadata.file_name) requestData.file_name = metadata.file_name;
    if (metadata.file_size) requestData.file_size = metadata.file_size;
    if (metadata.start_time) requestData.start_time = metadata.start_time;
    if (metadata.mode) requestData.mode = metadata.mode;
    if (metadata.chunk_size) requestData.chunk_size = metadata.chunk_size;
  }

  const response = await apiClient.post<{ file_url: string }>(
    '/storage/multipart/complete/',
    requestData,
    {
      // 文件合并阶段可能耗时较长（OBS服务端需要验证分片、合并文件、生成ETag等）
      // 大文件（1GB+）合并可能需要更长时间，设置15分钟超时
      // 避免前端过早超时导致503错误
      timeout: 900_000,  // 15 minutes
    }
  );
  return response.data;
};

/**
 * Proxy upload a small file via backend.
 */
export const proxyUpload = async (
  file: File,
  fileName: string,
  contentType: string,
  onUploadProgress?: (progressEvent: { loaded: number; total?: number }) => void,
  expectedMD5?: string,  // ✅ ETag校验用的预期MD5 (Hex格式)
  signal?: AbortSignal
): Promise<{ object_key: string; file_url: string; etag?: string }> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('file_name', fileName);
  formData.append('content_type', contentType);

  const headers: Record<string, string> = { 'Content-Type': 'multipart/form-data' };

  // ✅ 如果提供了expectedMD5，添加到请求头用于服务端校验
  if (expectedMD5) {
    headers['X-Expected-MD5'] = expectedMD5;
  }

  const response = await apiClient.post<{ object_key: string; file_url: string; etag?: string }>(
    '/storage/proxy-upload/',
    formData,
    {
      headers,
      timeout: 1_200_000, // 20 minutes - increased for slow networks in proxy mode
      onUploadProgress,
      signal,
    }
  );
  return response.data;
};

// 分片断流检测：浏览器→后端的发送进度只要停住，就主动 abort，不再等 axios 超时。
// 90s 无进展 = 链路已断（正常链路下 1MB 分片 3s 内传完）。
const PROXY_PART_STALL_TIMEOUT_MS = 90_000;
// 字节已全部发完、只剩等后端写 OBS 并返回 ETag 时，再给 120s 窗口。
const PROXY_PART_TAIL_TIMEOUT_MS = 120_000;
const PROXY_PART_WATCHDOG_INTERVAL_MS = 10_000;
const PROXY_PART_REQUEST_TIMEOUT_MS = 300_000;

/**
 * Proxy upload a single multipart part via backend.
 */
export const proxyUploadPart = async (
  partUrl: string,
  chunk: Blob,
  onUploadProgress?: (progressEvent: { loaded: number; total?: number }) => void,
  expectedMD5?: string,
  retryAttempt?: number,
  totalParts?: number,
  signal?: AbortSignal,
  uploadKey?: string
): Promise<{ etag: string }> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/octet-stream',
    'X-Part-Url': partUrl,
  };

  if (expectedMD5) {
    headers['X-Expected-MD5'] = expectedMD5;
  }

  if (retryAttempt !== undefined) {
    headers['X-Upload-Retry-Attempt'] = String(retryAttempt);
  }

  if (totalParts !== undefined) {
    headers['X-Total-Parts'] = String(totalParts);
  }

  // 让服务端能把分片进度回写到对应文件行（前端心跳失效时的兜底）
  if (uploadKey) {
    headers['X-Upload-Key'] = uploadKey;
  }

  // 独立 controller：既能被外部（用户取消/全局中止）打断，也能被 watchdog 打断
  const localController = new AbortController();
  const forwardAbort = () => localController.abort();
  if (signal) {
    if (signal.aborted) localController.abort();
    else signal.addEventListener('abort', forwardAbort, { once: true });
  }

  const totalBytes = chunk.size;
  let lastActivityAt = Date.now();
  let bytesSentAt = 0;
  let stalledFor = 0;

  const watchdog = setInterval(() => {
    const now = Date.now();
    // 字节已发完：只剩等响应，用更长的尾部窗口
    const window = bytesSentAt > 0 ? PROXY_PART_TAIL_TIMEOUT_MS : PROXY_PART_STALL_TIMEOUT_MS;
    const since = bytesSentAt > 0 ? bytesSentAt : lastActivityAt;
    if (now - since > window) {
      stalledFor = Math.round((now - since) / 1000);
      clearInterval(watchdog);
      localController.abort();
    }
  }, PROXY_PART_WATCHDOG_INTERVAL_MS);

  try {
    const response = await apiClient.post<{ etag: string }>(
      '/storage/proxy-upload/part/',
      chunk,
      {
        headers,
        timeout: PROXY_PART_REQUEST_TIMEOUT_MS,
        onUploadProgress: (e) => {
          lastActivityAt = Date.now();
          if (e.total && totalBytes > 0 && e.loaded >= totalBytes && bytesSentAt === 0) {
            bytesSentAt = Date.now();
          }
          onUploadProgress?.(e);
        },
        signal: localController.signal,
      }
    );
    return response.data;
  } catch (err) {
    if (stalledFor > 0) {
      const phase = bytesSentAt > 0 ? 'response' : 'upload';
      throw new Error(
        `UPLOAD_STALLED: no ${phase} progress for ${stalledFor}s (part of ${totalBytes} bytes)`
      );
    }
    throw err;
  } finally {
    clearInterval(watchdog);
    signal?.removeEventListener('abort', forwardAbort);
  }
};

/**
 * Notify backend of ETag mismatch failure for logging/debugging.
 * Used for both Direct and Proxy mode uploads when ETag validation fails.
 */
export const notifyETagMismatch = async (details: {
  file_name: string;
  file_size: number;
  mode: string;  // 'Direct' or 'Proxy'
  part_number?: number;  // For multipart uploads
  expected_md5: string;
  received_etag: string;
  timestamp: string;
  error_message?: string;
}): Promise<void> => {
  try {
    await apiClient.post(
      '/storage/etag-mismatch/',
      details,
      {
        timeout: 10_000, // 10 seconds
      }
    );
  } catch (error) {
    // Don't throw - notification failure shouldn't break upload flow
    uploadWarn('[API:ETagMismatch] Failed to notify backend:', error);
  }
};

/**
 * Notify backend of upload completion.
 * Used for Direct mode uploads to ensure backend logs all uploads.
 */
export const notifyUploadComplete = async (summary: {
  file_name: string;
  file_size: number;
  start_time: string;
  end_time: string;
  duration: string;
  mode: string;
  chunk_size: string;
  result: 'success' | 'failure';
  error?: string;
}): Promise<void> => {
  try {
    await apiClient.post(
      '/storage/upload-complete/',
      summary,
      {
        timeout: 10_000, // 10 seconds
      }
    );
  } catch (error) {
    // Don't throw - notification failure shouldn't break upload flow
    uploadWarn('[API:UploadComplete] Failed to notify backend:', error);
  }
};

/**
 * Delete a file from OBS storage.
 * Used to clean up uploaded files that are no longer needed.
 */
export const deleteObject = async (objectKey: string): Promise<void> => {
  await apiClient.delete(
    '/storage/objects/',
    {
      params: { object_key: objectKey },
      timeout: 60_000, // 1 minute
    }
  );
};

/**
 * Fix 4: Verify that a file exists in OBS and matches expected size.
 * Called after completeMultipartUpload to ensure file was properly saved.
 *
 * @param objectKey The OBS object key to verify
 * @param expectedSize Optional expected file size to validate
 * @returns Verification result with file metadata
 */
export const verifyFile = async (
  objectKey: string,
  expectedSize?: number
): Promise<{
  exists: boolean;
  size?: number;
  sizeMatch?: boolean;
  lastModified?: string;
  etag?: string;
  error?: string;
}> => {
  try {
    const response = await apiClient.post(
      '/storage/verify-file/',
      {
        object_key: objectKey,
        expected_size: expectedSize,
      },
      {
        timeout: 30_000, // 30 seconds
      }
    );
    return response.data;
  } catch (error) {
    uploadError('[API:VerifyFile] File verification failed:', error);
    return {
      exists: false,
      error: error instanceof Error ? error.message : 'Verification failed',
    };
  }
};

type UploadEventName =
  | 'fail_stuck_file'
  | 'probe_result'
  | 'upload_retry'
  | 'upload_error'
  | 'session_expired'
  | 'upload_mode_switch'
  | 'upload_stalled'
  | 'client_abort';

export const reportUploadEvent = async (
  event: UploadEventName,
  payload: {
    level?: 'info' | 'warn' | 'error';
    deliverable?: string;
    deliverable_id?: number | string;
    file_id?: number | string;
    file_name?: string;
    detail?: Record<string, unknown>;
  }
): Promise<void> => {
  try {
    await apiClient.post(
      '/storage/upload-event/',
      { event, ...payload },
      { timeout: 8_000 },
    );
  } catch {
    // fire-and-forget — never break the upload flow
  }
};
